import { ERROR_CODES } from './errorCodes';

/**
 * The text a staff member sees when the backend rejects a request, by the stable `code` of the response. The dashboard never
 * shows the `message` of the backend (technical English, ids, database text).
 *
 * A code with no entry falls back to the generic text of its status (see `useClient`). The routing codes
 * (APP_VERSION_TOO_LOW, OTA_BUNDLE_VERSION_TOO_LOW) are handled before any toast and have no text here, and
 * USER_NOT_A_GYM_USER, USER_NOT_AN_APP_USER, LOGIN_METHOD_MISMATCH, NO_ACTIVE_ROUTINE, SUPERVISOR_NOT_ASSIGNED and
 * SURVEY_ALREADY_ANSWERED only reach the patient app. The rate limiter answers with a lower case code that is not part of ERROR_CODES.
 * Keep the copy short, in plain Spanish: what happened and, when it helps, what to do next. No ids, no technical words.
 */
const TOO_MANY_REQUESTS_CODE = 'too_many_requests';

export const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  [ERROR_CODES.INVALID_CREDENTIALS]: 'El email o la contraseña no son correctos.',
  [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'No tienes permiso para hacer esto. Pide acceso a un administrador.',
  [ERROR_CODES.INVALID_OLD_PASSWORD]: 'La contraseña actual no es correcta.',
  [ERROR_CODES.RESET_TOKEN_EXPIRED]: 'Este enlace ha caducado. Pide uno nuevo.',
  [ERROR_CODES.RESET_TOKEN_INVALID]: 'Este enlace no es válido. Pide uno nuevo.',
  [ERROR_CODES.PASSWORD_SAME_AS_OLD]: 'La nueva contraseña debe ser distinta de la actual.',

  [ERROR_CODES.USER_INACTIVE]: 'Este usuario está desactivado.',
  [ERROR_CODES.USER_EMAIL_EXISTS]: 'Ya existe un usuario con este email.',
  [ERROR_CODES.USER_ALREADY_HAS_RECOVERY_LINK]: 'Ya enviamos un enlace de recuperación. Revisa el correo.',
  [ERROR_CODES.USER_IS_SUPERVISOR]: 'Este usuario es responsable de clientes. Reasígnalos antes.',
  [ERROR_CODES.USER_ALREADY_REGISTERED]: 'Este usuario ya está registrado.',
  [ERROR_CODES.USER_ALREADY_INVITED]: 'Este cliente ya está en la app o tiene una invitación pendiente.',
  [ERROR_CODES.EXTERNAL_USER_NO_EMAIL]: 'Este cliente no tiene email. Añádelo antes de enviar la invitación.',
  [ERROR_CODES.LAST_ORG_ADMIN]: 'Debe quedar al menos un administrador en la organización.',
  [ERROR_CODES.USER_HAS_FUTURE_RESERVATIONS]: 'Este usuario tiene reservas futuras. Cancélalas o muévelas antes.',
  [ERROR_CODES.USER_NOT_FOUND]: 'Este usuario ya no existe. Recarga la página.',
  [ERROR_CODES.EXTERNAL_USER_NOT_FOUND]: 'Este cliente ya no existe. Recarga la página.',

  [ERROR_CODES.RESERVATION_NOT_FOUND]: 'Esta reserva ya no existe. Recarga la página.',
  [ERROR_CODES.RESERVATION_TYPE_NOT_FOUND]: 'Este tipo de reserva ya no existe.',
  [ERROR_CODES.BRANCH_NOT_FOUND]: 'Este centro ya no existe. Recarga la página.',
  [ERROR_CODES.RESERVATION_FULL]: 'La sesión está completa.',
  [ERROR_CODES.RESERVATION_IS_IN_REST_HOUR]: 'Esa hora es de descanso del centro.',
  [ERROR_CODES.RESERVATION_IS_OUTSIDE_ALLOWED_HOURS]: 'Esa hora está fuera del horario de reservas.',
  [ERROR_CODES.BRANCH_HAS_CONFLICTING_RESERVATION]: 'Ya hay una reserva a esa hora en el centro.',
  [ERROR_CODES.EXTERNAL_USER_HAS_CONFLICTING_RESERVATIONS]: 'El cliente ya tiene otra reserva a esa hora.',
  [ERROR_CODES.EXTERNAL_USER_ALREADY_IN_RESERVATION]: 'El cliente ya está en esta reserva.',
  [ERROR_CODES.PARTICIPANT_NOT_FOUND]: 'Esta persona ya no está en la reserva.',
  [ERROR_CODES.PARTICIPANT_NOT_IN_QUEUE]: 'Esta persona ya no está en la lista de espera.',
  [ERROR_CODES.PARTICIPANT_ALREADY_CANCELED]: 'Esta persona ya tenía la reserva cancelada.',
  [ERROR_CODES.RESERVATION_ALREADY_CANCELED]: 'Esta reserva ya estaba cancelada.',
  [ERROR_CODES.RESERVATION_NOT_IN_FUTURE]: 'Solo se pueden cancelar reservas futuras.',
  [ERROR_CODES.RESERVATION_CANCELLATION_TIME_LIMIT_PASSED]: 'Ya ha pasado el plazo para cancelar esta reserva.',
  [ERROR_CODES.RESERVATION_ALREADY_STARTED]: 'Esta reserva ya ha empezado.',
  [ERROR_CODES.CANNOT_CLONE_CANCELED_RESERVATION]: 'No se puede duplicar una reserva cancelada.',
  [ERROR_CODES.RESERVATION_TYPE_NOT_CLONEABLE]: 'Este tipo de reserva no se puede duplicar.',
  [ERROR_CODES.INVALID_DATE_RANGE]: 'La hora de fin debe ser posterior a la de inicio.',
  [ERROR_CODES.RESERVATION_NOT_ALLOWED_BY_TYPE]: 'Esta reserva no cumple las reglas de su tipo. Revisa los datos.',

  [ERROR_CODES.COUPON_NOT_ENOUGH_USES]: 'El bono no tiene sesiones suficientes.',
  [ERROR_CODES.COUPON_INACTIVE]: 'Este bono está desactivado.',
  [ERROR_CODES.COUPON_IN_USE]: 'El bono ya está asignado a clientes: no se pueden cambiar el precio ni los usos.',
  [ERROR_CODES.COUPON_NOT_FOUND]: 'Este bono ya no existe. Recarga la página.',
  [ERROR_CODES.USER_COUPON_NOT_FOUND]: 'Este bono ya no existe. Recarga la página.',

  [ERROR_CODES.RESOURCE_WITH_SAME_NAME_EXISTS]: 'Ya existe otro con ese nombre o dirección. Cámbialo e inténtalo de nuevo.',
  [ERROR_CODES.RESOURCE_IN_USE]: 'No se puede borrar porque se está usando.',
  [ERROR_CODES.ROLE_NOT_FOUND]: 'Este rol ya no existe. Recarga la página.',

  [ERROR_CODES.ROUTINE_NOT_FOUND]: 'Esta rutina ya no existe. Recarga la página.',
  [ERROR_CODES.ROUTINE_ALREADY_ASSIGNED]: 'El cliente ya tiene una rutina activa o programada.',
  [ERROR_CODES.ROUTINE_CANNOT_FINISH]: 'Esta rutina no se puede finalizar.',
  [ERROR_CODES.EXERCISE_NOT_FOUND]: 'Este ejercicio ya no existe. Recarga la página.',

  [ERROR_CODES.SIGNED_DOCUMENT_NOT_FOUND]: 'Este documento ya no existe.',
  [ERROR_CODES.SIGNED_DOCUMENT_ALREADY_REVOKED]: 'Este documento ya estaba revocado.',
  [ERROR_CODES.SIGNING_DATA_MISSING]: 'Falta la firma o el DNI para firmar.',
  [ERROR_CODES.TICKET_NOT_FOUND]: 'Esta incidencia ya no existe.',
  [ERROR_CODES.FEATURE_NOT_ENABLED]: 'Esta función no está activada para tu organización. Contacta con soporte.',

  [ERROR_CODES.SURVEY_FORM_INCOMPLETE]: 'La encuesta necesita al menos una pregunta activa con su texto en todos los idiomas.',
  [ERROR_CODES.SURVEY_ALREADY_ACTIVE]: 'Ya hay una encuesta en curso. Ciérrala antes de lanzar otra.',
  [ERROR_CODES.SURVEY_LAUNCH_NOT_ACTIVE]: 'Esta encuesta ya no está en curso. Recarga la página.',
  [ERROR_CODES.SURVEY_TRANSLATION_UNAVAILABLE]: 'La traducción automática no está disponible ahora. Escribe el texto a mano.',
  [ERROR_CODES.SURVEY_TRANSLATION_FAILED]: 'No se ha podido traducir el texto. Inténtalo de nuevo en unos minutos.',

  [ERROR_CODES.VALIDATION_FAILED]: 'Revisa los datos del formulario.',
  [ERROR_CODES.FILE_UPLOAD_INVALID]: 'No se ha podido subir el archivo. Prueba con otro.',
  [TOO_MANY_REQUESTS_CODE]: 'Demasiados intentos. Espera un minuto.',
};

/** Used when the request got no answer at all (offline, DNS, timeout). */
export const NETWORK_ERROR_MESSAGE = 'No hay conexión. Revisa tu internet e inténtalo de nuevo.';
/** Used for a 5xx that has no text of its own. */
export const SERVER_ERROR_MESSAGE = 'Algo ha fallado. Inténtalo de nuevo en unos minutos.';
/** Used for any other failure that has no text of its own. */
export const GENERIC_ERROR_MESSAGE = 'No se ha podido completar la acción. Inténtalo de nuevo.';
